import { useState, useEffect, useRef } from "react";
import Layout from "../components/Layout";
import api from "../services/api";
import { useAuth } from "../context/AuthContext";

export default function SettingsPage() {
  const { user } = useAuth();
  const fileRef  = useRef(null);

  const [form, setForm] = useState({
    description:              "",
    whatsapp:                 "",
    instagram:                "",
    logo:                     "",
    scheduling_min_hours:     2,
    scheduling_max_days:      14,
    scheduling_same_day:      true,
  });

  const [preview,  setPreview]  = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState(null);

  const showToast = (message, type = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  useEffect(() => {
    api.get("/shop/settings")
      .then((res) => {
        setForm({
          description:          res.data.description          || "",
          whatsapp:             res.data.whatsapp             || "",
          instagram:            res.data.instagram            || "",
          logo:                 res.data.logo                 || "",
          scheduling_min_hours: res.data.scheduling_min_hours ?? 2,
          scheduling_max_days:  res.data.scheduling_max_days  ?? 14,
          scheduling_same_day:  res.data.scheduling_same_day  ?? true,
        });
        if (res.data.logo) setPreview(res.data.logo);
      })
      .catch(() => showToast("Failed to load settings", "error"))
      .finally(() => setLoading(false));
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.size > 512000) {
      showToast("Image too large. Please use an image under 500KB.", "error");
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const base64 = ev.target.result;
      setPreview(base64);
      setForm((prev) => ({ ...prev, logo: base64 }));
    };
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.post("/shop/settings", form);
      showToast("Settings saved successfully.");
    } catch (err) {
      showToast(
        err.response?.data?.message || "Failed to save settings.",
        "error"
      );
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <Layout>
      <div className="flex items-center justify-center h-64 text-gray-400 gap-3">
        <svg className="w-5 h-5 animate-spin" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
        </svg>
        Loading settings...
      </div>
    </Layout>
  );

  return (
    <Layout>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700;800&family=DM+Sans:wght@400;500;600&display=swap');`}</style>

      {toast && (
        <div className={`fixed top-5 right-5 z-50 flex items-center gap-3 px-5 py-3.5 rounded-xl shadow-xl text-sm font-medium ${
          toast.type === "error" ? "bg-red-500 text-white" : "bg-green-500 text-white"
        }`}>
          {toast.type === "error" ? "❌" : "✅"} {toast.message}
        </div>
      )}

      <div className="p-6 max-w-3xl mx-auto" style={{ fontFamily: "'DM Sans', sans-serif" }}>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900"
            style={{ fontFamily: "'Sora', sans-serif" }}>
            Business Settings
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Customize how your business appears to customers and configure delivery rules
          </p>
        </div>

        <div className="space-y-5">

          {/* Business info card — read only */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold mb-4 text-sm uppercase tracking-widest text-gray-400">
              Business Info
            </h2>
            <div className="grid grid-cols-2 gap-4">
              {[
                { label: "Business Name", value: user?.shop?.name },
                { label: "Phone",         value: user?.shop?.phone },
                { label: "Email",         value: user?.shop?.email },
                { label: "Address",       value: user?.shop?.address },
                { label: "Plan",          value: user?.shop?.plan },
                { label: "KRA PIN",       value: user?.shop?.kra_pin },
              ].map((row) => (
                <div key={row.label}>
                  <p className="text-gray-400 text-xs mb-0.5">{row.label}</p>
                  <p className="font-semibold text-gray-900 text-sm capitalize">
                    {row.value || "—"}
                  </p>
                </div>
              ))}
            </div>
            <p className="text-gray-400 text-xs mt-4">
              To update business info, contact TumaTrack support.
            </p>
          </div>

          {/* Logo upload */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold mb-1 text-sm uppercase tracking-widest text-gray-400">
              Business Logo
            </h2>
            <p className="text-gray-400 text-xs mb-5">
              Appears on the customer tracking page. Max 500KB, PNG or JPG.
            </p>
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-2xl border-2 border-dashed border-gray-200 flex items-center justify-center overflow-hidden shrink-0 bg-gray-50">
                {preview ? (
                  <img src={preview} alt="Logo preview" className="w-full h-full object-contain"/>
                ) : (
                  <span className="text-3xl">🏪</span>
                )}
              </div>
              <div className="flex-1">
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleLogoChange}
                  className="hidden"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="inline-flex items-center gap-2 border border-gray-200 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"/>
                  </svg>
                  {preview ? "Change Logo" : "Upload Logo"}
                </button>
                {preview && (
                  <button
                    onClick={() => { setPreview(null); setForm((p) => ({ ...p, logo: "" })); }}
                    className="ml-3 text-sm text-red-400 hover:text-red-600 transition-colors"
                  >
                    Remove
                  </button>
                )}
                <p className="text-gray-400 text-xs mt-2">
                  Square images work best (at least 200×200px)
                </p>
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold mb-1 text-sm uppercase tracking-widest text-gray-400">
              Business Description
            </h2>
            <p className="text-gray-400 text-xs mb-4">
              Short description shown on the tracking page. Max 500 characters.
            </p>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              maxLength={500}
              rows={3}
              placeholder="e.g. We deliver fresh flowers across Nairobi. Same-day delivery available."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 resize-none"
            />
            <p className="text-gray-400 text-xs text-right mt-1">
              {form.description.length}/500
            </p>
          </div>

          {/* Contact links */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold mb-4 text-sm uppercase tracking-widest text-gray-400">
              Contact Links
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  WhatsApp Number
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">+254</span>
                  <input
                    value={form.whatsapp}
                    onChange={(e) => setForm({ ...form, whatsapp: e.target.value })}
                    placeholder="7XXXXXXXX"
                    className="w-full pl-14 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
                <p className="text-gray-400 text-xs mt-1">
                  Customers can WhatsApp you directly from the tracking page
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                  Instagram Handle
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">@</span>
                  <input
                    value={form.instagram}
                    onChange={(e) => setForm({ ...form, instagram: e.target.value })}
                    placeholder="yourbusiness"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Scheduled Delivery Settings */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold mb-1 text-sm uppercase tracking-widest text-gray-400">
              Scheduled Delivery Settings
            </h2>
            <p className="text-gray-400 text-xs mb-6">
              Control how customers can schedule deliveries in advance.
            </p>

            <div className="space-y-5">

              {/* Same day toggle */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-gray-900 text-sm">Allow same-day scheduling</p>
                  <p className="text-gray-400 text-xs mt-0.5">
                    Let customers schedule deliveries for later today
                  </p>
                </div>
                <button
                  onClick={() => setForm({ ...form, scheduling_same_day: !form.scheduling_same_day })}
                  className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
                    form.scheduling_same_day ? "bg-orange-500" : "bg-gray-200"
                  }`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform duration-200 ${
                    form.scheduling_same_day ? "translate-x-6" : "translate-x-0"
                  }`}/>
                </button>
              </div>

              <div className="border-t border-gray-100 pt-5 grid grid-cols-1 md:grid-cols-2 gap-5">

                {/* Min window */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Minimum notice required
                  </label>
                  <select
                    value={form.scheduling_min_hours}
                    onChange={(e) => setForm({ ...form, scheduling_min_hours: Number(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value={1}>1 hour ahead</option>
                    <option value={2}>2 hours ahead</option>
                    <option value={3}>3 hours ahead</option>
                    <option value={6}>6 hours ahead</option>
                    <option value={12}>12 hours ahead</option>
                    <option value={24}>1 day ahead</option>
                  </select>
                  <p className="text-gray-400 text-xs mt-1">
                    Earliest a delivery can be scheduled from now
                  </p>
                </div>

                {/* Max window */}
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
                    Maximum scheduling window
                  </label>
                  <select
                    value={form.scheduling_max_days}
                    onChange={(e) => setForm({ ...form, scheduling_max_days: Number(e.target.value) })}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                  >
                    <option value={3}>3 days ahead</option>
                    <option value={7}>7 days ahead</option>
                    <option value={14}>14 days ahead</option>
                    <option value={30}>30 days ahead</option>
                  </select>
                  <p className="text-gray-400 text-xs mt-1">
                    How far in advance a delivery can be scheduled
                  </p>
                </div>
              </div>

              {/* Preview */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 text-xs text-blue-700">
                <span className="font-semibold">Current rules: </span>
                Customers can schedule deliveries between{" "}
                <span className="font-semibold">{form.scheduling_min_hours} hour{form.scheduling_min_hours > 1 ? "s" : ""}</span> and{" "}
                <span className="font-semibold">{form.scheduling_max_days} days</span> from now.
                Same-day scheduling is{" "}
                <span className="font-semibold">{form.scheduling_same_day ? "enabled" : "disabled"}</span>.
              </div>
            </div>
          </div>

           {/* Change Password */}
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
            <h2 className="font-bold mb-1 text-sm uppercase tracking-widest text-gray-400">
              Change Password
            </h2>
            <p className="text-gray-400 text-xs mb-5">
              Update your login password. You'll need your current password to confirm.
            </p>
            <ChangePasswordForm showToast={showToast} />
          </div>

          {/* Tracking page preview hint */}
          <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5">
            <p className="text-blue-700 font-semibold text-sm mb-1">
              💡 Tracking Page Preview
            </p>
            <p className="text-blue-600 text-xs leading-relaxed">
              Your logo and description will appear on the customer tracking page under
              "Fulfilled by {user?.shop?.name}". Changes take effect immediately on new tracking links.
            </p>
          </div>

          {/* Save button */}
          <div className="flex justify-end pb-8">
            <button
              onClick={handleSave}
              disabled={saving}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-xl transition-colors"
            >
              {saving ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
                  </svg>
                  Saving...
                </>
              ) : (
                "Save Settings"
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

function ChangePasswordForm({ showToast }) {
  const [form, setForm] = useState({
    current_password:      "",
    password:              "",
    password_confirmation: "",
  });
  const [saving, setSaving]   = useState(false);
  const [errors, setErrors]   = useState({});

  const handleSubmit = async () => {
    setErrors({});
    if (form.password !== form.password_confirmation) {
      setErrors({ password_confirmation: "Passwords do not match." });
      return;
    }
    setSaving(true);
    try {
      await api.post("/change-password", form);
      showToast("Password changed successfully.");
      setForm({ current_password: "", password: "", password_confirmation: "" });
    } catch (err) {
      const serverErrors = err.response?.data?.errors || {};
      const message      = err.response?.data?.message || "Failed to change password.";
      setErrors(serverErrors);
      showToast(message, "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-4">
      {[
        { key: "current_password",      label: "Current Password",  placeholder: "Enter current password" },
        { key: "password",              label: "New Password",       placeholder: "At least 8 characters" },
        { key: "password_confirmation", label: "Confirm New Password", placeholder: "Repeat new password" },
      ].map((f) => (
        <div key={f.key}>
          <label className="block text-xs font-semibold text-gray-600 mb-1.5 uppercase tracking-wide">
            {f.label}
          </label>
          <input
            type="password"
            value={form[f.key]}
            onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
            placeholder={f.placeholder}
            className={`w-full px-4 py-2.5 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 ${
              errors[f.key] ? "border-red-300 bg-red-50" : "border-gray-200"
            }`}
          />
          {errors[f.key] && (
            <p className="text-red-500 text-xs mt-1">{errors[f.key]}</p>
          )}
        </div>
      ))}
      <button
        onClick={handleSubmit}
        disabled={saving || !form.current_password || !form.password || !form.password_confirmation}
        className="inline-flex items-center gap-2 bg-slate-800 hover:bg-slate-900 disabled:opacity-50 text-white font-semibold px-5 py-2.5 rounded-xl text-sm transition-colors"
      >
        {saving ? (
          <>
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
            </svg>
            Updating...
          </>
        ) : (
          "Update Password"
        )}
      </button>
    </div>
  );
}